import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileText, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const TermsGate = () => {
  const { user, logout, setSession } = useAuth();
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || user.terms_accepted) return null;

  const agree = async () => {
    setBusy(true);
    try {
      await api.post("/auth/accept-terms");
      setSession(localStorage.getItem("kc_token"), { ...user, terms_accepted: true });
    } catch {
      toast.error("Couldn't record your acceptance — please try again");
      setBusy(false);
    }
  };

  const decline = () => {
    logout();
    toast.info("You must accept the terms to use The Kilted Courier portal.");
    window.location.href = "/login";
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="terms-gate">
      <div className="w-full max-w-lg rounded-xl bg-[#0f1b2d] p-6 text-slate-200 shadow-2xl sm:p-8">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-700/60">
          <ShieldCheck className="h-5 w-5 text-slate-200" />
        </div>
        <h2 className="mt-5 text-center font-heading text-2xl font-bold text-white">Please review and accept</h2>
        <p className="mt-1 text-center text-sm text-slate-400">Before you continue, acknowledge the terms below.</p>

        <div className="mt-6 space-y-4 text-sm leading-relaxed">
          <div className="flex gap-3">
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <p>I agree to the <b className="text-white">Terms &amp; Conditions</b> of The Kilted Courier, including that quoted prices are estimates, may change prior to booking confirmation, and are subject to our service policies.</p>
          </div>
          <div className="flex gap-3">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <p>I agree that my use of this app is subject to <b className="text-white">complete commercial confidentiality</b> — all quotes, pricing, routes, and information shared through this app are treated as confidential and may not be disclosed to third parties.</p>
          </div>
          <label className="flex cursor-pointer gap-3">
            <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)}
              data-testid="terms-checkbox" className="mt-1 h-4 w-4 accent-emerald-500" />
            <span>I have read and agree to the Terms &amp; Conditions and the commercial confidentiality terms above.</span>
          </label>
        </div>

        <div className="mt-6 space-y-2">
          <Button onClick={agree} disabled={!ticked || busy} data-testid="terms-agree-button"
            className="w-full bg-white font-semibold text-slate-900 hover:bg-slate-200 disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "I agree"}
          </Button>
          <Button onClick={decline} variant="outline" data-testid="terms-decline-button"
            className="w-full border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
            Decline
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TermsGate;
